import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Icon from '../../../components/dashboard/DashboardIcons';
import { toast } from '../../../lib/toast';
import { API, auth, Field, PageHead, PrimaryBtn, errMsg, inputCls, exportExcel, printArea, PrintStyle } from './shared';

function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return [start.toISOString().slice(0, 10), now.toISOString().slice(0, 10)];
}

export default function StudioReportsPage() {
  const [defFrom, defTo] = monthRange();
  const [dateFrom, setDateFrom] = useState(defFrom);
  const [dateTo, setDateTo] = useState(defTo);
  const [report, setReport] = useState(null);
  const [typeLabels, setTypeLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/coach-studio/config`).then((r) => {
      const map = {};
      (r.data.exercise_types || []).forEach((t) => { map[t.value] = t.label_ar; });
      setTypeLabels(map);
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/coach-studio/reports/period`, { ...auth(), params: { date_from: dateFrom, date_to: dateTo } });
      setReport(res.data);
    } catch (e) { setError(errMsg(e, 'تعذر تحميل التقرير')); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportReport = () => {
    if (!report) return;
    exportExcel([
      { المؤشر: 'جلسات مجدولة', القيمة: report.sessions_scheduled },
      { المؤشر: 'جلسات مكتملة', القيمة: report.sessions_completed },
      { المؤشر: 'جلسات ملغاة', القيمة: report.sessions_cancelled },
      { المؤشر: 'إجمالي دقائق التدريب', القيمة: report.total_training_minutes },
      { المؤشر: 'خطط نشطة', القيمة: report.active_plans },
      { المؤشر: 'خطط مكتملة', القيمة: report.completed_plans },
      { المؤشر: 'نسبة تنفيذ الخطط', القيمة: report.plan_execution_percent ?? '—' },
    ], `تقرير-فني-${dateFrom}-${dateTo}`);
  };

  return (
    <div className="space-y-4" data-testid="studio-reports-page">
      <PrintStyle />
      <PageHead title="التقارير الفنية" desc="مؤشرات مبنية على الجلسات والخطط والتمارين الفعلية فقط" action={
        <div className="flex gap-2">
          <button onClick={exportReport} disabled={!report} className="rounded-lg border border-slate-200 px-3 py-2 text-xs disabled:opacity-40" data-testid="studio-report-export-btn">تصدير Excel</button>
          <button onClick={printArea} className="rounded-lg border border-slate-200 px-3 py-2 text-xs" data-testid="studio-report-print-btn">طباعة</button>
        </div>
      } />

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <Field label="من تاريخ"><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} data-testid="studio-report-from-input" /></Field>
        <Field label="إلى تاريخ"><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} data-testid="studio-report-to-input" /></Field>
        <PrimaryBtn onClick={load} data-testid="studio-report-generate-btn"><Icon name="chart" className="w-4 h-4" /> عرض التقرير</PrimaryBtn>
      </div>

      {loading ? <div className="p-8 text-center text-sm text-slate-400" data-testid="studio-report-loading">جاري التحميل...</div> :
        error ? <div className="p-8 text-center text-sm text-red-500" data-testid="studio-report-error">{error}</div> :
        report && (
        <div className="print-area space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="studio-report-stats">
            <StatCard label="جلسات مجدولة" value={report.sessions_scheduled} testId="stat-scheduled" />
            <StatCard label="جلسات مكتملة" value={report.sessions_completed} testId="stat-completed" />
            <StatCard label="جلسات ملغاة" value={report.sessions_cancelled} testId="stat-cancelled" />
            <StatCard label="دقائق تدريب" value={report.total_training_minutes} testId="stat-minutes" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="studio-report-plans">
            <StatCard label="خطط نشطة" value={report.active_plans} testId="stat-active-plans" />
            <StatCard label="خطط مكتملة" value={report.completed_plans} testId="stat-completed-plans" />
            <StatCard label="نسبة تنفيذ الخطط" value={report.plan_execution_percent !== null ? `${report.plan_execution_percent}٪` : '—'} testId="stat-plan-exec" />
          </div>
          <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="studio-report-exercise-types">
            <h3 className="mb-3 text-sm font-bold text-slate-900">توزيع أنواع التمارين المستخدمة</h3>
            {Object.keys(report.exercise_type_distribution || {}).length === 0 ? (
              <p className="text-sm text-slate-400">لا توجد بيانات لهذه الفترة</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(report.exercise_type_distribution).map(([type, count]) => (
                  <span key={type} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{typeLabels[type] || type}: {count}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const StatCard = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={testId}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
  </div>
);
